<template>
  <dropdown ref="selectButtonDropdown" :is-disabled="isDisabled" :title="title" popper-min-width-size="medium">
    <dd-button
      :is-disabled="isDisabled"
      :color="selectedColor"
      :size="size"
      :is-submitting="false"
      type="flat"
      class="select-button__button"
    >
      <div class="select-button__button-content">
        <!-- PREFIX ICON -->
        <slot name="prefix" class="prefix" />
        
        <!-- SELECTED VALUE -->
        <slot v-if="selectedItem" :item="selectedItem" name="selectedValue">
          <span class="value">{{ selectedItem.name }}</span>
        </slot>

        <!-- ARROW -->
        <span v-if="!isDisabled" class="select-button__button-content-arrow">
          <i class="ddi ddi-caret-down" />
        </span>
      </div>
    </dd-button>
    <template #content>
      <div>
        <ul>
          <li v-for="value in options" :key="`${value.name}-${value.id}`" @click="handleSelect(value.id)">
            <span :class="{'selected' : isSelected(value)}">
              <chicklet
                :color="value.color"
                :size="size"
                type="flat"
                is-uppercase
              >
                <span>{{ value.name }}</span>
              </chicklet>
            </span>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { IIDNamePairWithColor } from '@/types/data-objects/global'

/* Options without a color show as grey */
type ISelectButtonOption = Omit<IIDNamePairWithColor, 'color'> & { color?: string }
import { availableSizeOptions } from '@/types/button-style-options'

/* API */

export default defineComponent({
  name: 'SelectButton',
  props: {
    modelValue: {
      type: [String, Number, Object, Array],
      default: null,
    },

    options: {
      type: Array as () => ISelectButtonOption[],
      default: (): ISelectButtonOption[] => [],
    },

    title: {
      type: String,
      default: '',
    },

    size: {
      type: String,
      default: 'large',
      validator: (value: string) => {
        return availableSizeOptions.includes(value)
      },
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      isOpen: false,
      internalValue: 0,
    }
  },

  computed: {
    selectedItem(): ISelectButtonOption {
      if (!this.internalValue) {
        return this.options[0]
      }
      return this.options.find(o => o.id === this.internalValue)
    },
    selectedColor(): string {
      return this.getColor(this.selectedItem)
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        this.internalValue = newVal
      },
    },
  },

  methods: {
    getColor(value: ISelectButtonOption) {
      return value ? value.color : 'grey'
    },

    handleSelect(value: number) {
      this.internalValue = value
      this.$emit('update:modelValue', value)
      // @ts-ignore
      this.$refs.selectButtonDropdown.handleClose()
    },
    isSelected(item: any) {
      if (this.selectedItem) {
        return item.id === this.selectedItem.id
      }
      return false
    },
  },
})
</script>

<style lang="scss">
.select-button {
  position: relative;

  &__button {
    max-width: 100%;
    display: flex;
    align-items: center;
    padding: 0 8px;

    > span {
      width: 100%;
    }

    .prefix-icon {
      width: 16px;
      height: 16px;
    }

    .prefix {
      margin-right: 4px;
    }

    .value {
      @include single-line-ellipsis;
      display: inline-block;
      @include lettering--h5;
      color: inherit;
      line-height: 1;
    }
  }

  &__button-content {
    display: flex;
    align-items: center;
    width: 100%;
  }

  &__button-content-arrow {
    padding-left: 4px;

    .ddi {
      font-size: $dd2-size--dropdown-caret;
      line-height: 14px;
    }
  }
}
</style>
